"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { createClient } from "@/lib/supabase/client";
import { createVenueAsStaff } from "@/app/booking/venues/actions";

interface CreateVenueDialogProps {
  triggerButton?: React.ReactNode;
  onVenueCreated?: () => void;
  /**
   * booking: booking staff with the venues_admin feature; the venue is
   * added to their venues and opens in the booking portal's settings.
   */
  portal?: "superadmin" | "booking";
}

export function CreateVenueDialog({
  triggerButton,
  onVenueCreated,
  portal = "superadmin",
}: CreateVenueDialogProps) {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [venueName, setVenueName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateVenue = async () => {
    if (!venueName.trim()) return;

    setIsCreating(true);
    if (portal === "booking") {
      const result = await createVenueAsStaff(venueName);
      if (!result.success || !result.venueId) {
        toast.error(result.error ?? "Failed to create venue");
        setIsCreating(false);
        return;
      }
      toast.success("Venue created successfully");
      // The new venue is in the user's token only after a refresh; then a
      // full load of its settings, so the menu and venue switcher include it.
      // The refresh is best effort: never wait on it for more than 3 seconds.
      await Promise.race([
        createClient()
          .auth.refreshSession()
          .catch(() => undefined),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
      window.location.assign(
        result.redirectTo ?? `/booking/venue/${result.venueId}/settings`,
      );
      return;
    }
    try {
      const response = await fetch("/api/superadmin/venues", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: venueName,
          currency: "USD",
          status: "active",
          min_participant: 2,
          max_participant: 20,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error("API Error Response:", error);
        throw new Error(
          error.error || `Failed to create venue (${response.status})`,
        );
      }

      const data = await response.json();

      if (!data.venue || !data.venue.id) {
        throw new Error("Venue was created but no ID was returned");
      }

      toast.success("Venue created successfully");

      // Trigger refresh callback if provided
      if (onVenueCreated) {
        onVenueCreated();
      }

      // Navigate to the venue edit page with autoSelect parameter
      // The dialog will close automatically when the page navigates
      // Keep isCreating as true - it will reset when the component unmounts
      router.push(`/superadmin/venues/${data.venue.id}?autoSelect=true`);
    } catch (error) {
      console.error("Error creating venue:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to create venue",
      );
      // Only set isCreating to false if there's an error
      setIsCreating(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Prevent closing the dialog while creating
        if (!isCreating) {
          setIsOpen(open);
        }
      }}
    >
      <DialogTrigger asChild>
        {triggerButton || (
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Create new venue
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-xl font-semibold">
            Create new venue
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Enter a name for the new venue. You can configure additional
            settings after creation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium text-gray-700">
              Venue name *
            </Label>
            <Input
              id="name"
              value={venueName}
              onChange={(e) => setVenueName(e.target.value)}
              placeholder="e.g., Downtown Game Center"
              className="w-full"
              disabled={isCreating}
            />
            <p className="text-xs text-gray-500">
              You can add translations and other details in the venue settings
            </p>
          </div>
        </div>

        <DialogFooter className="pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => {
              setIsOpen(false);
              setVenueName("");
            }}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateVenue}
            disabled={!venueName.trim() || isCreating}
          >
            {isCreating ? (
              <>Creating...</>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create venue
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
